import React from "react";
import { AlertTriangle } from "lucide-react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, clientRef: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // A render crash is a CLIENT error — generate its OWN reference rather than
    // surfacing an unrelated (stale) API request id. Console only; no user-facing internals.
    const clientRef = "C" + Math.random().toString(16).slice(2, 9).toUpperCase();
    this.setState({ clientRef });
    // eslint-disable-next-line no-console
    console.error("CaterCTRL UI error boundary caught an error", error?.name, info?.componentStack?.split("\n")?.[1]?.trim());
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  handleSignOut = () => {
    try { localStorage.removeItem("cc_token"); } catch (e) { /* ignore */ }
    window.location.assign("/login");
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const ref = this.state.clientRef;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="app-error-boundary">
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="mx-auto h-12 w-12 rounded-xl bg-brand-light flex items-center justify-center mb-4">
            <AlertTriangle className="text-brand-dark" size={22} />
          </div>
          <h1 className="font-display text-xl font-bold text-slate-800">Something went wrong</h1>
          <p className="text-sm text-slate-500 mt-2">We couldn't load this part of CaterCTRL. Please try again.</p>
          {ref && <p className="text-[11px] font-mono text-slate-400 mt-4" data-testid="error-reference">Reference: {String(ref)}</p>}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button onClick={this.handleReload} data-testid="error-reload" className="bg-brand hover:bg-brand-hover text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors">Try again</button>
            <button onClick={this.handleSignOut} data-testid="error-signout" className="text-slate-500 hover:text-slate-700 text-sm font-semibold px-4 py-2">Sign out</button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
